import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MarketingNav, MarketingFooter } from '../../components/marketing/MarketingLayout';
import { CheckCircle2, Play, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DUBAI_BG = 'https://images.unsplash.com/photo-1640184713837-02e0d30b0031?w=1920&q=40';

function Reveal({ children, delay = 0 }) {
  return <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay }}>{children}</motion.div>;
}

function PricingCard({ title, subtitle, price, period, features, popular, cta }) {
  return (
    <div className={`relative p-8 lg:p-10 rounded-2xl border-2 transition-all duration-300 h-full flex flex-col ${popular 
      ? 'border-[#0f172a] bg-white shadow-[0_8px_32px_rgba(15,23,42,0.12)]' 
      : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-md'}`}
      data-testid={`pricing-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0f172a] text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">Most Popular</div>}
      <div className="mb-6">
        <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
        {subtitle && <p className="text-xs text-[#d4a84b] font-semibold">{subtitle}</p>}
      </div>
      <div className="mb-6">
        <span className="text-4xl font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>{price}</span>
        {period && <span className="text-[#71717A] ml-1 text-sm">{period}</span>}
      </div>
      <ul className="space-y-3 mb-8 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#52525B]">
            <CheckCircle2 size={16} className="text-[#10B981] mt-0.5 shrink-0" />{f}
          </li>
        ))}
      </ul>
      <button className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors text-sm ${popular 
        ? 'bg-[#0f172a] text-white hover:bg-[#1e293b]' 
        : 'bg-white text-[#0A0A0A] border border-zinc-200 hover:bg-zinc-50'}`}>
        {cta}
      </button>
    </div>
  );
}

export default function PricingPage() {
  const [tab, setTab] = useState('subscription');

  const handleDemo = async () => {
    try {
      const res = await fetch(`${API}/api/auth/demo-login`, { method: 'POST' });
      const data = await res.json();
      localStorage.setItem('erp_token', data.access_token);
      localStorage.setItem('erp_user', JSON.stringify({ ...data.user, is_demo: true }));
      localStorage.setItem('erp_demo_mode', 'true');
      window.location.href = '/dashboard';
    } catch { alert('Demo unavailable.'); }
  };

  return (
    <div className="bg-[#FCFCFC] text-[#0A0A0A] min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <MarketingNav />

      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${DUBAI_BG})`, opacity: 0.04 }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4a84b] mb-3">Pricing</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Flexible Plans. No Surprises.
            </h1>
            <p className="text-base lg:text-lg text-[#52525B] max-w-2xl mx-auto">
              Choose a monthly subscription or purchase the app outright. 
              Both options include all security features, backups, and compliance tools.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tab Toggle */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-white border border-zinc-200 rounded-xl p-1 shadow-sm">
              <button onClick={() => setTab('subscription')} className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${tab === 'subscription' ? 'bg-[#0f172a] text-white shadow-md' : 'text-[#52525B] hover:text-[#0A0A0A]'}`} data-testid="pricing-tab-subscription">
                Subscription Plan
              </button>
              <button onClick={() => setTab('buy')} className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${tab === 'buy' ? 'bg-[#0f172a] text-white shadow-md' : 'text-[#52525B] hover:text-[#0A0A0A]'}`} data-testid="pricing-tab-buy">
                Buy the App
              </button>
            </div>
          </div>

          {tab === 'subscription' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Reveal><PricingCard title="Starter" subtitle="For Small Teams" price="Contact Us" features={['Single company', 'Up to 5 users', 'Core modules (Sales, HR, Purchases)', 'Basic reports & analytics', 'Email support', 'UAE VAT compliance']} cta="Get Started" /></Reveal>
              <Reveal delay={0.1}><PricingCard title="Business" subtitle="For Growing SMEs" price="Contact Us" popular features={['Single company', 'Up to 25 users', 'All modules included', 'Advanced analytics & forecasting', 'Multi-currency support', 'GST/VAT/Corporate Tax', 'QR document verification', 'Priority support', 'Training manual access']} cta="Contact Us" /></Reveal>
              <Reveal delay={0.2}><PricingCard title="Enterprise / Pro" subtitle="For Accountants & Auditors" price="Contact Us" features={['Multi-company (BizCore Pro™)', 'Unlimited users', 'All Business features', 'Accountant Hub dashboard', 'Client workspace isolation', 'Bulk tax filing tools', 'Dedicated support', 'White-label branding', 'API access']} cta="Contact Sales" /></Reveal>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <Reveal><PricingCard title="BizCore™ Standard" subtitle="One-Time Purchase" price="Contact Us" features={['Perpetual license', 'Single company', 'All core modules', '1 year free updates', 'Self-hosted or cloud', 'Setup & migration support', 'Source code escrow', 'UAE VAT & Tax compliance']} cta="Request Quote" /></Reveal>
              <Reveal delay={0.1}><PricingCard title="BizCore Pro™" subtitle="One-Time Purchase" price="Contact Us" popular features={['Perpetual license', 'Multi-company (unlimited)', 'Accountant Hub included', 'All modules + Pro features', '1 year free updates', 'Priority setup & migration', 'White-label option', 'Dedicated account manager', 'Custom development support']} cta="Request Quote" /></Reveal>
            </div>
          )}

          <Reveal>
            <div className="text-center mt-10 space-y-3">
              <p className="text-sm text-[#71717A]">All plans include SSL encryption, automated backups, full audit trails, and RBAC.</p>
              <div className="flex items-center justify-center gap-4">
                <button onClick={handleDemo} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0f172a] hover:text-[#d4a84b] transition-colors">
                  <Play size={14} fill="currentColor" /> Try Demo First
                </button>
                <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0f172a] hover:text-[#d4a84b] transition-colors">
                  Contact Sales <ChevronRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
